import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Card, Col, Row, Select, Space, Form, DatePicker, Spin } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import ExportIcon from "../../../assets/images/export-svgrepo-com (1).svg";
import {
  TotalCustomerChart,
  CustomerPieChart,
  LoadingTimeChart,
  CustomerPieChartTOP,
  LoadingTimeChartTop,
} from "../CustomerReportAI/CustomerChartAI";
import { CALL_API } from "../../../Common/api";
import { handleDownloadChart } from "../../../Reusablecomponents/Export/Exportpdf";
import { withNexusReportShell } from "../NexusReportShell";

// Nexus Customer Report: same page and charts as the AI Customer Report, built
// from the Nexus trip events (POST nexus/customerreport). Customer = consignee.

const { Option } = Select;
const { RangePicker } = DatePicker;

const Customerreportstyle = styled.div`
  .head {
    color: #5f6d8c;
  }
  .total {
    display: flex;
    align-items: center;
    justify-content: space-between !important;
  }
  .Custom {
    display: flex;
    align-items: center;
    gap: 20px;
  }
  .dropstyle {
    margin-top: 20px !important;
    width: 251px !important;
  }
  .dropstyle1 {
    height: 36px;
    width: 251px !important;
  }
  .plc .ant-select-selection-placeholder {
    color: black !important;
  }
  .plc .ant-select-arrow {
    color: black;
  }
  .ant-card-head {
    border-bottom: none;
  }
  .chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const topcustomercolors = ["#00BC13", "#DEE640", "#FC8954", "#0090EA", "#89C8FF"];
const bottomcustomercolors = ["#04A093", "#40E6E4", "#0090BC", "#89C0FF", "#22A5F6"];

const Duration = [
  { label: "Current Month", value: "current_month" },
  { label: "Current Year", value: "current_year" },
  { label: "Last Month", value: "last_month" },
  { label: "Today", value: "today" },
];

const rangeFor = (duration) => {
  if (duration === "today") return [dayjs(), dayjs()];
  if (duration === "last_month")
    return [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")];
  if (duration === "current_year") return [dayjs().startOf("year"), dayjs().endOf("year")];
  return [dayjs().startOf("month"), dayjs().endOf("month")];
};

const exportIcon = (allowed, onClick) => (
  <img
    src={ExportIcon}
    alt="Export"
    style={{
      width: "20px",
      height: "20px",
      cursor: allowed ? "pointer" : "not-allowed",
      backgroundColor: "#EBF7ED",
      borderRadius: "6px",
    }}
    onClick={() => {
      if (!allowed) return;
      onClick();
    }}
  />
);

const NexusCustomerReport = () => {
  const reduxPermissions = useSelector((state) => state?.authTracking?.myPermissionList || {});
  const exportAllowed = useMemo(
    () => Array.isArray(reduxPermissions?.NE_CUSTRPT) && reduxPermissions.NE_CUSTRPT.includes("EX"),
    [reduxPermissions],
  );

  const [chartData, setChartData] = useState({});
  const [duration, setDuration] = useState("current_month");
  const [selectedDate, setSelectedDate] = useState(rangeFor("current_month"));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSelectedDate(rangeFor(duration));
  }, [duration]);

  const getCustomerChart = useCallback(async () => {
    if (!selectedDate?.[0] || !selectedDate?.[1]) return;
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "nexus/customerreport", {
        dateFrom: dayjs(selectedDate[0]).format("YYYY-MM-DD"),
        dateTo: dayjs(selectedDate[1]).format("YYYY-MM-DD"),
      });
      setChartData(data || {});
    } catch (error) {
      console.error("[NexusCustomerReport] fetch error:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    getCustomerChart();
  }, [getCustomerChart]);

  const list = (v) => (Array.isArray(v) ? v : []);

  return (
    <div>
      <Customerreportstyle>
        <Col sm={24} xl={24} md={24}>
          <Card>
            <div className="total">
              <div>Overiew</div>
              <div className="Custom">
                <div className="dropstyle">
                  <Form.Item label="" name="dropdown">
                    <Select
                      placeholder="Custom Range"
                      className="plc"
                      style={{ width: "240px" }}
                      value={duration}
                      onChange={setDuration}
                    >
                      {Duration.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item?.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className="dropstyle1">
                  <Space direction="vertical" size={12}>
                    <RangePicker
                      value={selectedDate}
                      onChange={(date) => date && setSelectedDate(date)}
                    />
                  </Space>
                </div>
              </div>
            </div>
          </Card>
        </Col>
        <div>
          <div id="Export Chart">
            <Col sm={24} xl={24} md={24} className="mt-3">
              <Spin spinning={loading}>
                <Card
                  title={
                    <div className="chart-header">
                      <span>
                        Total Customer (Consignee)
                        <span style={{ fontSize: "32px", marginLeft: "20px", alignContent: "center" }}>
                          {list(chartData?.Customer_Total).length}
                        </span>
                      </span>
                      <div>
                        {exportIcon(exportAllowed, () => handleDownloadChart("Export Chart", "Total Customer"))}
                      </div>
                    </div>
                  }
                >
                  <TotalCustomerChart data={list(chartData?.Customer_Total)} width="100%" height={350} />
                </Card>
              </Spin>
            </Col>
          </div>

          <div id="Export Chart1">
            <Card
              className="w-100 mt-3"
              title={
                <div className="chart-header">
                  <div className="total">
                    <span>Offloading Cycle (Hr)</span>
                  </div>
                  <div>
                    {exportIcon(exportAllowed, () => handleDownloadChart("Export Chart1", "Offloading Time"))}
                  </div>
                </div>
              }
            >
              <Row gutter={16}>
                <Col sm={24} md={12} xl={12}>
                  <Spin spinning={loading}>
                    <Card>
                      <h6>Top 5 Customers</h6>
                      <CustomerPieChartTOP data={list(chartData?.OffLoadingCycleTop)} colors={topcustomercolors} />
                    </Card>
                  </Spin>
                </Col>
                <Col sm={24} md={12} xl={12}>
                  <Spin spinning={loading}>
                    <Card>
                      <h6>Bottom 5 Customers</h6>
                      <CustomerPieChart
                        data={list(chartData?.OffLoadingCycleBottom)}
                        width="100%"
                        height={350}
                        colors={bottomcustomercolors}
                      />
                    </Card>
                  </Spin>
                </Col>
              </Row>
            </Card>
          </div>

          <div id="Export Chart4">
            <Card
              className="mt-3"
              title={
                <div className="chart-header">
                  <div className="total">
                    <span>Loading Cycle(Hr)</span>
                  </div>
                  <div>
                    {exportIcon(exportAllowed, () => handleDownloadChart("Export Chart4", "Loading Time"))}
                  </div>
                </div>
              }
            >
              <Row gutter={16}>
                <Col sm={24} md={12} xl={12}>
                  <Spin spinning={loading}>
                    <Card>
                      <h6>Top 5 Customers</h6>
                      {list(chartData?.LoadingCycleTop).length > 0 && (
                        <LoadingTimeChartTop
                          data={chartData.LoadingCycleTop}
                          color="#126DF6"
                          width="100%"
                          height={250}
                        />
                      )}
                    </Card>
                  </Spin>
                </Col>
                <Col sm={24} md={12} xl={12}>
                  <Spin spinning={loading}>
                    <Card>
                      <h6>Bottom 5 Customers</h6>
                      {list(chartData?.LoadingCycleBottom).length > 0 && (
                        <LoadingTimeChart
                          data={chartData.LoadingCycleBottom}
                          color="#09D982"
                          width="100%"
                          height={250}
                        />
                      )}
                    </Card>
                  </Spin>
                </Col>
              </Row>
            </Card>
          </div>
        </div>
      </Customerreportstyle>
    </div>
  );
};

export default withNexusReportShell(NexusCustomerReport);
